import type { ValidationError } from "class-validator";
import {
  IsArray,
  IsBoolean,
  IsEmail,
  IsInt,
  Min,
  Max,
  Matches,
  ValidateIf,
  validate,
} from "class-validator";

export interface NotificationSettingsFormState {
  notification_emails: string[];
  push_phones: string[];
  sms_escalation_enabled: boolean;
  sms_escalation_delay_minutes: number;
  sms_escalation_phones: string[];
}

const PHONE_PATTERN = /^\+7\d{10}$/;

export function sanitizeNotificationForm(
  form: NotificationSettingsFormState,
): NotificationSettingsFormState {
  const sms_escalation_phones = form.sms_escalation_phones
    .map((phone) => phone.trim())
    .filter(Boolean);

  return {
    ...form,
    notification_emails: form.notification_emails
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean),
    push_phones: form.push_phones.map((phone) => phone.trim()).filter(Boolean),
    sms_escalation_phones,
    sms_escalation_enabled:
      form.sms_escalation_enabled && sms_escalation_phones.length > 0,
  };
}

class NotificationSettingsDto {
  @IsArray()
  @IsEmail({}, { each: true, message: "Некорректный email" })
  notification_emails: string[] = [];

  @IsArray()
  @Matches(PHONE_PATTERN, {
    each: true,
    message: "Телефон должен быть в формате +7XXXXXXXXXX",
  })
  push_phones: string[] = [];

  @IsBoolean()
  sms_escalation_enabled = false;

  @ValidateIf((o: NotificationSettingsDto) => o.sms_escalation_enabled)
  @IsInt({ message: "Укажите целое число минут" })
  @Min(1, { message: "Минимум 1 минута" })
  @Max(10080, { message: "Максимум 10080 минут (7 дней)" })
  sms_escalation_delay_minutes = 30;

  @ValidateIf((o: NotificationSettingsDto) => o.sms_escalation_enabled)
  @IsArray()
  @Matches(PHONE_PATTERN, {
    each: true,
    message: "Телефон должен быть в формате +7XXXXXXXXXX",
  })
  sms_escalation_phones: string[] = [];
}

export function mapValidationErrors(
  errors: ValidationError[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const error of errors) {
    if (error.constraints) {
      result[error.property] = Object.values(error.constraints)[0] ?? "";
    }
    for (const child of error.children ?? []) {
      if (child.constraints) {
        result[`${error.property}.${child.property}`] =
          Object.values(child.constraints)[0] ?? "";
      }
    }
  }
  return result;
}

export async function validateNotificationSettings(
  form: NotificationSettingsFormState,
): Promise<Record<string, string>> {
  const dto = Object.assign(
    new NotificationSettingsDto(),
    sanitizeNotificationForm(form),
  );

  const errors = await validate(dto);
  return mapValidationErrors(errors);
}
